import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

// ── Types ─────────────────────────────────────────────────────────────────────

interface Educator {
  id: string; service_number: string;
  full_name_en: string | null; full_name_ms: string | null; preferred_name: string | null;
  designation: string | null; post_title_ms: string | null;
  employment_type: string; employment_status: string; career_band: string | null;
  school_name: string | null; current_school_name: string | null;
  email: string | null; phone: string | null; photo_url: string | null;
  service_start_date: string | null; confirmation_date: string | null;
  nationality: string | null; gender: string | null; date_of_birth: string | null;
  emergency_contact: { name?: string; phone?: string; relationship?: string } | null;
  is_sped_certified: boolean; abe_certifications: unknown[];
  retirement_eligible_date: string | null;
}

interface Qualification {
  id: string; qual_type: string; title: string;
  institution: string | null; country: string | null;
  year_obtained: number | null; major: string | null;
  is_highest: boolean; is_verified: boolean; doc_url: string | null;
  verified_at: string | null;
}

interface Posting {
  id: string; school_id: string; school_name: string | null;
  posting_type: string; position: string; department: string | null;
  effective_date: string; end_date: string | null;
  is_current: boolean; order_number: string | null;
}

interface Document {
  id: string; doc_type: string; doc_name: string;
  doc_url: string; version: number; is_current_version: boolean; created_at: string;
}

// ── Tab type ──────────────────────────────────────────────────────────────────
type Tab = 'profile' | 'postings' | 'qualifications' | 'documents';

const TABS: { id: Tab; label: string }[] = [
  { id: 'profile',        label: 'Profil' },
  { id: 'postings',       label: 'Penempatan' },
  { id: 'qualifications', label: 'Kelayakan' },
  { id: 'documents',      label: 'Dokumen' },
];

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  ACTIVE:    { label: 'Aktif',       color: '#4ade80' },
  INACTIVE:  { label: 'Tidak Aktif', color: '#9ca3af' },
  SUSPENDED: { label: 'Digantung',   color: '#fb923c' },
  RETIRED:   { label: 'Bersara',     color: '#60a5fa' },
};

const EMP_TYPE_LABELS: Record<string, string> = {
  PERMANENT: 'Tetap / Permanent', CONTRACT: 'Kontrak / Contract',
  SECONDED: 'Dipinjamkan / Seconded', TEMP: 'Sementara / Temporary', PART_TIME: 'Separuh Masa / Part-time',
};

// ── Sub-forms ─────────────────────────────────────────────────────────────────

function AddQualificationForm({ educatorId, onDone, onCancel }: {
  educatorId: string; onDone: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState({
    qualType: '', title: '', institution: '', country: '',
    yearObtained: '', major: '', isHighest: false, docUrl: '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState<string | null>(null);

  async function submit() {
    if (!form.qualType.trim()) { setErr('Sila pilih jenis kelayakan. / Select qualification type.'); return; }
    if (!form.title.trim())    { setErr('Sila masukkan tajuk kelayakan. / Enter qualification title.'); return; }
    if (form.yearObtained && (isNaN(Number(form.yearObtained)) || Number(form.yearObtained) < 1900 || Number(form.yearObtained) > new Date().getFullYear())) {
      setErr(`Tahun tidak sah (1900–${new Date().getFullYear()}). / Invalid year.`); return;
    }
    setSaving(true); setErr(null);
    try {
      await apiPost(`/api/educators/${educatorId}/qualifications`, {
        qualType: form.qualType.trim(), title: form.title.trim(),
        institution: form.institution.trim() || undefined,
        country:     form.country.trim() || undefined,
        yearObtained: form.yearObtained ? Number(form.yearObtained) : undefined,
        major:        form.major.trim() || undefined,
        isHighest:    form.isHighest,
        docUrl:       form.docUrl.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Gagal menyimpan. / Failed to save.');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: borderRadius.lg, padding: spacing.lg, border: `1px solid ${colors.neutral.gray800}`, marginTop: spacing.md }}>
      <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
        + Tambah Kelayakan / Add Qualification
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          <FormField label="Jenis / Type *" style={{ flex: '1 1 140px' }}>
            <select value={form.qualType} onChange={(e) => setForm((f) => ({ ...f, qualType: e.target.value }))} disabled={saving} style={inputSt()}>
              <option value="">-- Pilih --</option>
              {['PhD','Masters','Degree','Diploma','Certificate','PGDE','Other'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Tajuk / Title *" style={{ flex: '3 1 200px' }}>
            <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} disabled={saving} style={inputSt()} maxLength={300} />
          </FormField>
        </div>
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          <FormField label="Institusi / Institution" style={{ flex: '2 1 160px' }}>
            <input type="text" value={form.institution} onChange={(e) => setForm((f) => ({ ...f, institution: e.target.value }))} disabled={saving} style={inputSt()} maxLength={300} />
          </FormField>
          <FormField label="Negara / Country" style={{ flex: '1 1 120px' }}>
            <input type="text" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} disabled={saving} style={inputSt()} maxLength={100} />
          </FormField>
          <FormField label="Tahun / Year" style={{ flex: '1 1 80px' }}>
            <input type="number" value={form.yearObtained} onChange={(e) => setForm((f) => ({ ...f, yearObtained: e.target.value }))} disabled={saving} style={inputSt()} min={1900} max={new Date().getFullYear()} />
          </FormField>
        </div>
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormField label="Major" style={{ flex: '2 1 160px' }}>
            <input type="text" value={form.major} onChange={(e) => setForm((f) => ({ ...f, major: e.target.value }))} disabled={saving} style={inputSt()} maxLength={200} />
          </FormField>
          <FormField label="URL Dokumen / Doc URL" style={{ flex: '2 1 160px' }}>
            <input type="url" value={form.docUrl} onChange={(e) => setForm((f) => ({ ...f, docUrl: e.target.value }))} disabled={saving} style={inputSt()} maxLength={500} placeholder="https://…" />
          </FormField>
          <label style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, cursor: saving ? 'not-allowed' : 'pointer', marginTop: '18px', fontSize: typography.fontSize.sm, color: colors.neutral.white }}>
            <input type="checkbox" checked={form.isHighest} onChange={(e) => setForm((f) => ({ ...f, isHighest: e.target.checked }))} disabled={saving} />
            Tertinggi / Highest
          </label>
        </div>
      </div>
      {err && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{err}</p>}
      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
        <Button variant="primary" size="sm" onClick={() => void submit()} disabled={saving} loading={saving}>
          {saving ? 'Menyimpan…' : 'Simpan / Save'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Batal</Button>
      </div>
    </div>
  );
}

function AddDocumentForm({ educatorId, onDone, onCancel }: {
  educatorId: string; onDone: () => void; onCancel: () => void;
}) {
  const [form, setForm] = useState({ docType: '', docName: '', docUrl: '' });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState<string | null>(null);

  async function submit() {
    if (!form.docType.trim()) { setErr('Sila pilih jenis dokumen. / Select document type.'); return; }
    if (!form.docName.trim()) { setErr('Sila masukkan nama dokumen. / Enter document name.'); return; }
    if (!form.docUrl.trim())  { setErr('Sila masukkan URL dokumen. / Enter document URL.'); return; }
    try { new URL(form.docUrl.trim()); } catch {
      setErr('URL dokumen tidak sah. / Invalid document URL.'); return;
    }
    setSaving(true); setErr(null);
    try {
      await apiPost(`/api/educators/${educatorId}/documents`, {
        docType: form.docType.trim(), docName: form.docName.trim(), docUrl: form.docUrl.trim(),
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Gagal menyimpan. / Failed to save.');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: borderRadius.lg, padding: spacing.lg, border: `1px solid ${colors.neutral.gray800}`, marginTop: spacing.md }}>
      <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
        + Tambah Dokumen / Add Document
      </p>
      <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
        <FormField label="Jenis / Type *" style={{ flex: '1 1 130px' }}>
          <select value={form.docType} onChange={(e) => setForm((f) => ({ ...f, docType: e.target.value }))} disabled={saving} style={inputSt()}>
            <option value="">-- Pilih --</option>
            {['IC','Passport','Teaching_Cert','Degree_Cert','Contract','Medical','Other'].map((t) => (
              <option key={t} value={t}>{t.replace('_', ' ')}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Nama Dokumen / Name *" style={{ flex: '2 1 180px' }}>
          <input type="text" value={form.docName} onChange={(e) => setForm((f) => ({ ...f, docName: e.target.value }))} disabled={saving} style={inputSt()} maxLength={300} />
        </FormField>
        <FormField label="URL Dokumen *" style={{ flex: '2 1 200px' }}>
          <input type="url" value={form.docUrl} onChange={(e) => setForm((f) => ({ ...f, docUrl: e.target.value }))} disabled={saving} style={inputSt()} placeholder="https://…" maxLength={500} />
        </FormField>
      </div>
      {err && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{err}</p>}
      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
        <Button variant="primary" size="sm" onClick={() => void submit()} disabled={saving} loading={saving}>
          {saving ? 'Menyimpan…' : 'Simpan / Save'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Batal</Button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function EducatorDetailPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === "en";
  const { id } = useParams<{ id: string }>();
  const [tab,     setTab]     = useState<Tab>('profile');
  const [educator, setEducator] = useState<Educator | null>(null);
  const [quals,   setQuals]   = useState<Qualification[]>([]);
  const [postings, setPostings] = useState<Posting[]>([]);
  const [docs,    setDocs]    = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);
  const [showAddQual,   setShowAddQual]   = useState(false);
  const [showAddDoc,    setShowAddDoc]    = useState(false);
  const [verifyingQual, setVerifyingQual] = useState<string | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  // Load educator profile
  useEffect(() => {
    if (!id) { setError('ID pendidik tidak sah. / Invalid educator ID.'); return; }
    setLoading(true); setError(null);
    apiGet<Educator>(`/api/educators/${id}`)
      .then(setEducator)
      .catch((err) => setError(err instanceof Error ? err.message : 'Ralat tidak diketahui.'))
      .finally(() => setLoading(false));
  }, [id]);

  // Load sub-resource when tab changes
  const loadTab = useCallback(async (t: Tab) => {
    if (!id || t === 'profile') return;
    setSubLoading(true); setSubError(null);
    try {
      if (t === 'qualifications') {
        setQuals(await apiGet<Qualification[]>(`/api/educators/${id}/qualifications`));
      } else if (t === 'postings') {
        setPostings(await apiGet<Posting[]>(`/api/educators/${id}/postings`));
      } else if (t === 'documents') {
        setDocs(await apiGet<Document[]>(`/api/educators/${id}/documents`));
      }
    } catch (err) {
      setSubError(err instanceof Error ? err.message : 'Ralat memuatkan data. / Load error.');
    } finally { setSubLoading(false); }
  }, [id]);

  useEffect(() => { void loadTab(tab); }, [tab, loadTab]);

  async function handleVerifyQual(qualId: string) {
    if (!id || verifyingQual) return;
    setVerifyingQual(qualId);
    try {
      await apiPost(`/api/educators/${id}/qualifications/${qualId}/verify`, {});
      setToast({ variant: 'success', message: 'Kelayakan disahkan. / Qualification verified.' });
      void loadTab('qualifications');
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Gagal. / Failed.' });
    } finally {
      setVerifyingQual(null);
    }
  }

  if (!id) return null;

  const name = educator?.preferred_name ?? educator?.full_name_en ?? educator?.full_name_ms ?? '—';
  const statusCfg = STATUS_CFG[educator?.employment_status ?? ''] ?? { label: educator?.employment_status ?? '', color: '#9ca3af' };

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Nav */}
          <div style={{ marginBottom: spacing.lg }}>
            <Link to="/admin/educators" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>
              ← Senarai Pendidik / Educator List
            </Link>
          </div>

          {/* Main error */}
          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171' }}>{error}</p>
            </div>
          )}

          {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && educator && (
            <>
              {/* Header card */}
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, boxShadow: shadows.xl, marginBottom: spacing.lg, display: 'flex', gap: spacing.lg, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Avatar */}
                <div style={{ width: '72px', height: '72px', borderRadius: borderRadius.full, backgroundColor: colors.neutral.gray800, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {educator.photo_url
                    ? <img src={educator.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    : <span style={{ color: colors.neutral.gray400, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold }}>
                        {name.charAt(0).toUpperCase()}
                      </span>
                  }
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.xs }}>
                    <h1 style={{ margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>{name}</h1>
                    <span style={{ padding: '2px 10px', backgroundColor: `${statusCfg.color}22`, color: statusCfg.color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
                      {statusCfg.label}
                    </span>
                    {educator.is_sped_certified && (
                      <span style={{ padding: '2px 10px', backgroundColor: 'rgba(167,139,250,0.1)', color: '#a78bfa', borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>SpEd</span>
                    )}
                  </div>
                  <p style={{ margin: `0 0 ${spacing.xs}`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                    {educator.service_number}
                    {educator.designation ? ` · ${educator.designation}` : ''}
                    {educator.post_title_ms ? ` (${educator.post_title_ms})` : ''}
                  </p>
                  <p style={{ margin: 0, color: colors.neutral.gray400, fontSize: typography.fontSize.xs }}>
                    {EMP_TYPE_LABELS[educator.employment_type] ?? educator.employment_type}
                    {educator.career_band ? ` · Kumpulan ${educator.career_band}` : ''}
                    {(educator.current_school_name ?? educator.school_name) ? ` · ${educator.current_school_name ?? educator.school_name}` : ''}
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    style={{
                      flex: 1, padding: `${spacing.sm} ${spacing.xs}`,
                      background: 'none',
                      borderBottom: tab === t.id ? `2px solid ${colors.primary.yellow}` : '2px solid transparent',
                      borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                      color: tab === t.id ? colors.primary.yellow : colors.neutral.gray400,
                      fontWeight: tab === t.id ? typography.fontWeight.semibold : typography.fontWeight.medium,
                      fontSize: typography.fontSize.sm, cursor: 'pointer',
                      fontFamily: typography.fontFamily.body, transition: 'color 0.15s',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div>
                {/* ── Profile ── */}
                {tab === 'profile' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                    <Section title="Maklumat Peribadi / Personal Info">
                      <Grid>
                        <Field label="Nama Penuh (EN)">{educator.full_name_en ?? '—'}</Field>
                        <Field label="Nama Penuh (MS)">{educator.full_name_ms ?? '—'}</Field>
                        <Field label="Nama Pilihan">{educator.preferred_name ?? '—'}</Field>
                        <Field label="Jantina / Gender">{educator.gender ?? '—'}</Field>
                        <Field label="Tarikh Lahir / DOB">{educator.date_of_birth ? new Date(educator.date_of_birth).toLocaleDateString(E ? 'en-GB' : 'ms-BN') : '—'}</Field>
                        <Field label="Warganegara / Nationality">{educator.nationality ?? '—'}</Field>
                        <Field label="E-mel / Email">{educator.email ?? '—'}</Field>
                        <Field label="Telefon / Phone">{educator.phone ?? '—'}</Field>
                      </Grid>
                    </Section>

                    {educator.emergency_contact && Object.keys(educator.emergency_contact).length > 0 && (
                      <Section title="Hubungi Kecemasan / Emergency Contact">
                        <Grid>
                          <Field label="Nama">{educator.emergency_contact.name ?? '—'}</Field>
                          <Field label="Telefon">{educator.emergency_contact.phone ?? '—'}</Field>
                          <Field label="Hubungan">{educator.emergency_contact.relationship ?? '—'}</Field>
                        </Grid>
                      </Section>
                    )}

                    <Section title="Maklumat Perkhidmatan / Service Info">
                      <Grid>
                        <Field label="No. Perkhidmatan">{educator.service_number}</Field>
                        <Field label="Jenis Lantikan">{EMP_TYPE_LABELS[educator.employment_type] ?? educator.employment_type}</Field>
                        <Field label="Tarikh Mula / Start Date">{educator.service_start_date ? new Date(educator.service_start_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN') : '—'}</Field>
                        <Field label={E ? "Confirmation Date" : "Tarikh Pengesahan"}>{educator.confirmation_date ? new Date(educator.confirmation_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN') : '—'}</Field>
                        <Field label="Kumpulan Kerjaya">{educator.career_band ?? '—'}</Field>
                        <Field label={E ? "Retirement Date" : "Tarikh Persaraan"}>{educator.retirement_eligible_date ? new Date(educator.retirement_eligible_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN') : '—'}</Field>
                      </Grid>
                    </Section>
                  </div>
                )}

                {/* ── Postings ── */}
                {tab === 'postings' && (
                  <>
                    {subError && <ErrorBox msg={subError} onRetry={() => void loadTab('postings')} />}
                    {subLoading && <LoadingBox />}
                    {!subLoading && !subError && postings.length === 0 && (
                      <EmptyBox icon="🏫" title="Tiada Rekod Penempatan" body="No posting history found. / Tiada sejarah penempatan." />
                    )}
                    {!subLoading && postings.map((p) => (
                      <div key={p.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.sm, border: `1px solid ${p.is_current ? 'rgba(247,208,0,0.3)' : colors.neutral.gray800}` }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.sm, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs }}>
                              {p.is_current && <Pill label="Semasa / Current" color={colors.primary.yellow} />}
                              <Pill label={p.posting_type} color="#60a5fa" />
                            </div>
                            <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{p.position}</p>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                              {p.school_name ?? p.school_id}
                              {p.department ? ` · ${p.department}` : ''}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                              {new Date(p.effective_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                              {p.end_date ? ` – ${new Date(p.end_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}` : ' – ' + (E ? 'Present' : 'Sekarang')}
                            </p>
                            {p.order_number && (
                              <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                                No. Arahan: {p.order_number}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* ── Qualifications ── */}
                {tab === 'qualifications' && (
                  <>
                    {isAdmin && !showAddQual && (
                      <div style={{ marginBottom: spacing.md }}>
                        <Button variant="secondary" size="sm" onClick={() => setShowAddQual(true)}>
                          + Tambah Kelayakan
                        </Button>
                      </div>
                    )}
                    {showAddQual && (
                      <AddQualificationForm
                        educatorId={id}
                        onDone={() => { setShowAddQual(false); void loadTab('qualifications'); setToast({ variant: 'success', message: 'Kelayakan ditambah. / Qualification added.' }); }}
                        onCancel={() => setShowAddQual(false)}
                      />
                    )}
                    {subError && <ErrorBox msg={subError} onRetry={() => void loadTab('qualifications')} />}
                    {subLoading && <LoadingBox />}
                    {!subLoading && !subError && quals.length === 0 && (
                      <EmptyBox icon="🎓" title="Tiada Kelayakan" body="No qualifications recorded. / Tiada kelayakan direkodkan." />
                    )}
                    {!subLoading && quals.map((q) => (
                      <div key={q.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.sm, border: `1px solid ${q.is_highest ? 'rgba(247,208,0,0.3)' : colors.neutral.gray800}` }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.sm, flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap' }}>
                              <Pill label={q.qual_type} color="#a78bfa" />
                              {q.is_highest && <Pill label="Tertinggi" color={colors.primary.yellow} />}
                              {q.is_verified
                                ? <Pill label="✓ Disahkan" color="#4ade80" />
                                : <Pill label="Belum Disahkan" color="#fb923c" />
                              }
                            </div>
                            <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{q.title}</p>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                              {q.institution ?? ''}
                              {q.country ? ` · ${q.country}` : ''}
                              {q.year_obtained ? ` · ${q.year_obtained}` : ''}
                              {q.major ? ` · ${q.major}` : ''}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
                            {q.doc_url && (
                              <a href={q.doc_url} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: typography.fontSize.xs }}>📎</a>
                            )}
                            {isAdmin && !q.is_verified && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => void handleVerifyQual(q.id)}
                                disabled={!!verifyingQual}
                                loading={verifyingQual === q.id}
                              >
                                {verifyingQual === q.id ? '…' : 'Sahkan'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* ── Documents ── */}
                {tab === 'documents' && (
                  <>
                    {isAdmin && !showAddDoc && (
                      <div style={{ marginBottom: spacing.md }}>
                        <Button variant="secondary" size="sm" onClick={() => setShowAddDoc(true)}>
                          + Tambah Dokumen
                        </Button>
                      </div>
                    )}
                    {showAddDoc && (
                      <AddDocumentForm
                        educatorId={id}
                        onDone={() => { setShowAddDoc(false); void loadTab('documents'); setToast({ variant: 'success', message: 'Dokumen ditambah. / Document added.' }); }}
                        onCancel={() => setShowAddDoc(false)}
                      />
                    )}
                    {subError && <ErrorBox msg={subError} onRetry={() => void loadTab('documents')} />}
                    {subLoading && <LoadingBox />}
                    {!subLoading && !subError && docs.length === 0 && (
                      <EmptyBox icon="📁" title="Tiada Dokumen" body="No documents uploaded. / Tiada dokumen dimuat naik." />
                    )}
                    {!subLoading && docs.map((d) => (
                      <div key={d.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.lg, padding: spacing.md, marginBottom: spacing.sm, display: 'flex', alignItems: 'center', gap: spacing.md, border: `1px solid ${colors.neutral.gray800}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs }}>
                            <Pill label={d.doc_type} color="#60a5fa" />
                            {d.is_current_version && <Pill label="Semasa" color="#4ade80" />}
                          </div>
                          <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.doc_name}</p>
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            v{d.version} · {new Date(d.created_at).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                          </p>
                        </div>
                        <a href={d.doc_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm">↗ Buka</Button>
                        </a>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, boxShadow: shadows.lg }}>
      <p style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>{title}</p>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: spacing.md }}>{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ margin: `0 0 2px`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{label}</p>
      <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.white }}>{children}</p>
    </div>
  );
}

function FormField({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={style}>
      <label style={{ display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs }}>{label}</label>
      {children}
    </div>
  );
}

function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
      {label}
    </span>
  );
}

function ErrorBox({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
    </div>
  );
}

function LoadingBox() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>;
}

function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}

function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
